import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../../constants';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        contact: formData.contact,
        message: formData.message,
      };

      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams,
        {
          publicKey: EMAILJS_CONFIG.publicKey,
        }
      );
      
      setFormData({
        name: '',
        email: '',
        contact: '',
        message: '',
      });
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Unable to send your message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3 sm:p-4"
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        className="relative w-full max-w-[340px] sm:max-w-[450px] md:max-w-[550px] rounded-[7.5px] overflow-hidden"
        style={{
          height: 'auto',
          minHeight: '500px',
          maxHeight: '90vh',
          background: 'rgba(0, 0, 0, 0.5)',
          border: '0.75px solid #FFFFFF',
          boxShadow: '0px 0px 3px rgba(0, 0, 0, 0.25)',
          backdropFilter: 'blur(15px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2
          className="text-center font-semibold text-[20px] sm:text-[23px] md:text-[25.5px] leading-[28px] sm:leading-[33px] md:leading-[37px] text-white px-4 pt-6 sm:pt-7 md:pt-[29.25px] pb-4 sm:pb-5 md:pb-6"
          style={{
            fontFamily: 'Murecho',
            fontWeight: 600,
            textShadow: '0px 3px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          Let's get in touch
        </h2>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 md:px-[55px] pb-6 sm:pb-8 md:pb-10">
          {/* Your Name Field */}
          <div className="mb-4 sm:mb-5 md:mb-6">
            <label
              className="block font-medium text-[11px] sm:text-[11.5px] md:text-[12px] leading-[16px] md:leading-[17px] text-white mb-2"
              style={{
                fontFamily: 'Murecho',
                fontWeight: 500,
              }}
            >
              Your Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className="w-full h-[35px] sm:h-[36px] md:h-[37.5px] rounded-[6px] border bg-white/90 px-3 sm:px-[10px] md:px-[11.25px] py-3 text-[10px] sm:text-[10.25px] md:text-[10.5px] text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-blue-400 transition-all"
              style={{
                boxSizing: 'border-box',
                boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.02)',
                backdropFilter: 'blur(1.5px)',
                border: errors.name ? '1.125px solid #FF6B6B' : '1.125px solid #DFDFDF',
                fontFamily: 'Murecho',
              }}
            />
            {errors.name && (
              <span className="block text-[#FF6B6B] text-[9px] mt-1">
                {errors.name}
              </span>
            )}
          </div>

          {/* Your Email Field */}
          <div className="mb-4 sm:mb-5 md:mb-6">
            <label
              className="block font-medium text-[11px] sm:text-[11.5px] md:text-[12px] leading-[16px] md:leading-[17px] text-white mb-2"
              style={{
                fontFamily: 'Murecho',
                fontWeight: 500,
              }}
            >
              Your Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              className="w-full h-[35px] sm:h-[36px] md:h-[37.5px] rounded-[6px] border bg-white/90 px-3 sm:px-[10px] md:px-[11.25px] py-3 text-[10px] sm:text-[10.25px] md:text-[10.5px] text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-blue-400 transition-all"
              style={{
                boxSizing: 'border-box',
                boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.02)',
                backdropFilter: 'blur(1.5px)',
                border: errors.email ? '1.125px solid #FF6B6B' : '1.125px solid #DFDFDF',
                fontFamily: 'Murecho',
              }}
            />
            {errors.email && (
              <span className="block text-[#FF6B6B] text-[9px] mt-1">
                {errors.email}
              </span>
            )}
          </div>

          {/* Your Contact Field */}
          <div className="mb-4 sm:mb-5 md:mb-6">
            <label
              className="block font-medium text-[11px] sm:text-[11.5px] md:text-[12px] leading-[16px] md:leading-[17px] text-white mb-2"
              style={{
                fontFamily: 'Murecho',
                fontWeight: 500,
              }}
            >
              Your Contact
            </label>
            <input
              type="tel"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              placeholder="+91 9876543210"
              className="w-full h-[35px] sm:h-[36px] md:h-[37.5px] rounded-[6px] border bg-white/90 px-3 sm:px-[10px] md:px-[11.25px] py-3 text-[10px] sm:text-[10.25px] md:text-[10.5px] text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-blue-400 transition-all"
              style={{
                boxSizing: 'border-box',
                boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.02)',
                backdropFilter: 'blur(1.5px)',
                border: errors.contact ? '1.125px solid #FF6B6B' : '1.125px solid #DFDFDF',
                fontFamily: 'Murecho',
              }}
            />
            {errors.contact && (
              <span className="block text-[#FF6B6B] text-[9px] mt-1">
                {errors.contact}
              </span>
            )}
          </div>

          {/* Message Field */}
          <div className="mb-5 sm:mb-6 md:mb-8">
            <label
              className="block font-medium text-[11px] sm:text-[11.5px] md:text-[12px] leading-[16px] md:leading-[17px] text-white mb-2"
              style={{
                fontFamily: 'Murecho',
                fontWeight: 500,
              }}
            >
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Tell us more about your inquiry..."
              className="w-full h-[70px] sm:h-[75px] md:h-[81px] rounded-[6px] border bg-white/90 px-3 sm:px-[11px] md:px-[12.75px] py-2 sm:py-[9px] md:py-[9.75px] text-[10px] sm:text-[10.25px] md:text-[10.5px] text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-blue-400 transition-all resize-none"
              style={{
                boxSizing: 'border-box',
                boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.02)',
                backdropFilter: 'blur(1.5px)',
                border: errors.message ? '1.125px solid #FF6B6B' : '1.125px solid #DFDFDF',
                fontFamily: 'Murecho',
              }}
            />
            {errors.message && (
              <span className="block text-[#FF6B6B] text-[9px] mt-1">
                {errors.message}
              </span>
            )}
          </div>

          {/* Send Message Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto sm:min-w-[200px] md:min-w-[240px] h-[48px] sm:h-[50px] md:h-[53.25px] mx-auto font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap block"
            style={{
              padding: '2.8125px 20px',
              background: isSubmitting 
                ? 'linear-gradient(274.68deg, #D44426 -5.46%, #D89A1F 138.47%)'
                : 'linear-gradient(274.68deg, #F05736 -5.46%, #FBB12F 138.47%)',
              boxShadow: '0px 2.25px 2.25px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(1.125px)',
              borderRadius: '6.75px',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontFamily: 'Murecho',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          {errors.submit && (
            <div className="mt-3 text-center text-[#FF6B6B] text-[11px]">
              {errors.submit}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
